'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CareerLevel = 'Entry' | 'Mid' | 'Senior' | 'Executive';

type SummaryStyle = 'Results-Driven' | 'Skills-Focused' | 'Story-Driven';

interface GeneratedSummary {
  style: SummaryStyle;
  description: string;
  text: string;
}

interface FormData {
  jobTitle: string;
  yearsOfExperience: string;
  industry: string;
  skills: string;
  achievement: string;
  careerLevel: CareerLevel;
}

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function parseSkills(raw: string): string[] {
  return raw
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatSkillsList(skills: string[]): string {
  if (skills.length === 0) return '';
  if (skills.length === 1) return skills[0];
  if (skills.length === 2) return `${skills[0]} and ${skills[1]}`;
  return `${skills.slice(0, -1).join(', ')}, and ${skills[skills.length - 1]}`;
}

function yearsLabel(years: string): string {
  const n = parseInt(years, 10);
  if (isNaN(n) || n <= 0) return '';
  if (n === 1) return '1 year';
  return `${n} years`;
}

function yearsPlus(years: string): string {
  const n = parseInt(years, 10);
  if (isNaN(n) || n <= 0) return '';
  return `${n}+`;
}

/* ------------------------------------------------------------------ */
/*  Industry-specific language banks                                   */
/* ------------------------------------------------------------------ */

const industryContext: Record<string, {
  domains: string[];
  impacts: string[];
  verbs: string[];
}> = {
  Technology: {
    domains: ['software development', 'digital innovation', 'technology solutions', 'product engineering', 'cloud infrastructure'],
    impacts: ['scalable solutions', 'system reliability', 'product adoption', 'technical architecture', 'engineering velocity'],
    verbs: ['architected', 'engineered', 'deployed', 'automated', 'optimized'],
  },
  Finance: {
    domains: ['financial services', 'capital markets', 'risk management', 'financial operations', 'investment strategy'],
    impacts: ['portfolio performance', 'regulatory compliance', 'revenue growth', 'risk mitigation', 'cost optimization'],
    verbs: ['managed', 'analyzed', 'forecasted', 'restructured', 'audited'],
  },
  Healthcare: {
    domains: ['patient care', 'clinical operations', 'health systems', 'medical research', 'healthcare delivery'],
    impacts: ['patient outcomes', 'care quality', 'operational efficiency', 'clinical workflows', 'compliance standards'],
    verbs: ['improved', 'coordinated', 'implemented', 'streamlined', 'standardized'],
  },
  Marketing: {
    domains: ['brand strategy', 'digital marketing', 'market positioning', 'campaign management', 'growth marketing'],
    impacts: ['brand awareness', 'lead generation', 'conversion rates', 'customer engagement', 'market share'],
    verbs: ['launched', 'drove', 'scaled', 'positioned', 'amplified'],
  },
  Education: {
    domains: ['curriculum development', 'academic programs', 'educational technology', 'student success', 'learning design'],
    impacts: ['student outcomes', 'program effectiveness', 'learning engagement', 'curriculum quality', 'institutional goals'],
    verbs: ['developed', 'mentored', 'designed', 'facilitated', 'assessed'],
  },
  Sales: {
    domains: ['business development', 'revenue generation', 'client relationships', 'sales strategy', 'account management'],
    impacts: ['revenue targets', 'pipeline growth', 'client retention', 'deal closure', 'market expansion'],
    verbs: ['closed', 'negotiated', 'cultivated', 'exceeded', 'secured'],
  },
  Engineering: {
    domains: ['systems engineering', 'product development', 'technical design', 'infrastructure', 'process engineering'],
    impacts: ['system performance', 'design efficiency', 'project delivery', 'quality standards', 'operational uptime'],
    verbs: ['designed', 'built', 'tested', 'integrated', 'validated'],
  },
  Design: {
    domains: ['user experience', 'visual design', 'product design', 'design systems', 'creative direction'],
    impacts: ['user satisfaction', 'design consistency', 'conversion optimization', 'brand identity', 'usability metrics'],
    verbs: ['crafted', 'designed', 'prototyped', 'iterated', 'conceptualized'],
  },
  HR: {
    domains: ['talent acquisition', 'people operations', 'organizational development', 'employee experience', 'workforce planning'],
    impacts: ['employee retention', 'hiring efficiency', 'team performance', 'culture development', 'talent pipeline'],
    verbs: ['recruited', 'developed', 'implemented', 'championed', 'transformed'],
  },
  Other: {
    domains: ['cross-functional initiatives', 'operational excellence', 'strategic planning', 'process improvement', 'project management'],
    impacts: ['organizational goals', 'team productivity', 'process efficiency', 'stakeholder satisfaction', 'business outcomes'],
    verbs: ['led', 'managed', 'delivered', 'drove', 'spearheaded'],
  },
};

/* ------------------------------------------------------------------ */
/*  Level-appropriate vocabulary                                       */
/* ------------------------------------------------------------------ */

const levelVocab: Record<CareerLevel, {
  descriptors: string[];
  openings: string[];
  strengthWords: string[];
}> = {
  Entry: {
    descriptors: ['motivated', 'detail-oriented', 'eager', 'dedicated', 'enthusiastic', 'adaptable'],
    openings: ['Recent graduate', 'Emerging professional', 'Aspiring', 'Early-career professional', 'Motivated professional'],
    strengthWords: ['foundational knowledge', 'academic training', 'hands-on experience', 'internship experience', 'coursework'],
  },
  Mid: {
    descriptors: ['results-oriented', 'versatile', 'analytical', 'collaborative', 'proactive', 'resourceful'],
    openings: ['Experienced', 'Accomplished', 'Seasoned', 'Proven', 'Skilled'],
    strengthWords: ['proven ability', 'hands-on expertise', 'demonstrated success', 'solid background', 'progressive experience'],
  },
  Senior: {
    descriptors: ['strategic', 'high-performing', 'innovative', 'cross-functional', 'influential', 'forward-thinking'],
    openings: ['Senior', 'Strategic', 'High-impact', 'Established', 'Distinguished'],
    strengthWords: ['deep expertise', 'track record', 'thought leadership', 'comprehensive knowledge', 'strategic vision'],
  },
  Executive: {
    descriptors: ['visionary', 'transformational', 'enterprise-level', 'C-suite', 'globally-minded', 'results-driven'],
    openings: ['Executive', 'Transformational leader', 'Visionary', 'Accomplished executive', 'Dynamic leader'],
    strengthWords: ['executive leadership', 'organizational transformation', 'enterprise strategy', 'board-level experience', 'P&L ownership'],
  },
};

/* ------------------------------------------------------------------ */
/*  Achievement integration helpers                                    */
/* ------------------------------------------------------------------ */

function buildAchievementClause(achievement: string): string {
  if (!achievement.trim()) return '';
  const cleaned = achievement.trim().replace(/[.!,;]+$/, '');
  const connectors = [
    `notably ${cleaned.charAt(0).toLowerCase() + cleaned.slice(1)}`,
    `including ${cleaned.charAt(0).toLowerCase() + cleaned.slice(1)}`,
    `highlighted by ${cleaned.charAt(0).toLowerCase() + cleaned.slice(1)}`,
    `with a key accomplishment of ${cleaned.charAt(0).toLowerCase() + cleaned.slice(1)}`,
    `such as ${cleaned.charAt(0).toLowerCase() + cleaned.slice(1)}`,
  ];
  return pickRandom(connectors);
}

/* ------------------------------------------------------------------ */
/*  Core summary generation — Results-Driven style                     */
/* ------------------------------------------------------------------ */

function generateResultsDriven(data: FormData): string {
  const { jobTitle, yearsOfExperience, industry, skills, achievement, careerLevel } = data;
  const parsedSkills = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const lvl = levelVocab[careerLevel];
  const yrs = yearsLabel(yearsOfExperience);
  const yrsPlus = yearsPlus(yearsOfExperience);
  const achieveClause = buildAchievementClause(achievement);
  const impact = pickRandom(indCtx.impacts);
  const domain = pickRandom(indCtx.domains);

  const templates = [
    // Template 1
    () => {
      const descriptor = pickRandom(lvl.descriptors);
      const opener = yrs
        ? `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} with ${yrs} of experience in ${domain}`
        : `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} with a strong background in ${domain}`;
      const middle = parsedSkills.length > 0
        ? `Proven ability to leverage ${formatSkillsList(parsedSkills)} to drive ${impact}`
        : `Proven ability to drive ${impact} through strategic execution`;
      const ending = achieveClause
        ? `Demonstrated measurable results, ${achieveClause}.`
        : `Consistently delivers measurable results that align with organizational objectives.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 2
    () => {
      const verb = pickRandom(indCtx.verbs);
      const opener = yrsPlus
        ? `${yrsPlus} years of progressive experience as a ${jobTitle} specializing in ${domain}`
        : `Dedicated ${jobTitle} specializing in ${domain}`;
      const skillPart = parsedSkills.length > 0
        ? `with core competencies in ${formatSkillsList(parsedSkills)}`
        : `with a focus on delivering high-impact outcomes`;
      const ending = achieveClause
        ? `Successfully ${verb} initiatives that improved ${impact}, ${achieveClause}.`
        : `Known for having ${verb} initiatives that significantly improved ${impact}.`;
      return `${opener}, ${skillPart}. ${ending}`;
    },
    // Template 3
    () => {
      const strength = pickRandom(lvl.strengthWords);
      const opener = yrs
        ? `${pickRandom(lvl.openings)} ${jobTitle} bringing ${yrs} of ${strength} in the ${industry} sector`
        : `${pickRandom(lvl.openings)} ${jobTitle} bringing ${strength} in the ${industry} sector`;
      const skillPart = parsedSkills.length > 0
        ? `Adept at applying ${formatSkillsList(parsedSkills)} to deliver quantifiable improvements in ${impact}`
        : `Adept at delivering quantifiable improvements in ${impact}`;
      const ending = achieveClause
        ? `Track record of success, ${achieveClause}.`
        : `Committed to driving results that directly contribute to business growth.`;
      return `${opener}. ${skillPart}. ${ending}`;
    },
    // Template 4
    () => {
      const descriptor = pickRandom(lvl.descriptors);
      const opener = yrs
        ? `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} with ${yrs} driving ${impact} across ${domain}`
        : `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} dedicated to driving ${impact} across ${domain}`;
      const middle = parsedSkills.length > 0
        ? `Expert in ${formatSkillsList(parsedSkills)} with a focus on translating strategy into measurable outcomes`
        : `Focused on translating strategic initiatives into measurable outcomes`;
      const ending = achieveClause
        ? `Delivered significant impact, ${achieveClause}.`
        : `Recognized for consistently exceeding performance benchmarks.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 5
    () => {
      const verb = pickRandom(indCtx.verbs);
      const impact2 = pickRandom(indCtx.impacts);
      const opener = yrsPlus
        ? `Performance-driven ${jobTitle} with ${yrsPlus} years of experience delivering results in ${industry}`
        : `Performance-driven ${jobTitle} delivering results in ${industry}`;
      const middle = parsedSkills.length > 0
        ? `Core strengths in ${formatSkillsList(parsedSkills)}, with a proven ability to enhance ${impact} and ${impact2}`
        : `Proven ability to enhance ${impact} and ${impact2} through strategic leadership`;
      const ending = achieveClause
        ? `Has ${verb} key initiatives, ${achieveClause}.`
        : `Consistently ${verb} key initiatives that exceed organizational expectations.`;
      return `${opener}. ${middle}. ${ending}`;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Core summary generation — Skills-Focused style                     */
/* ------------------------------------------------------------------ */

function generateSkillsFocused(data: FormData): string {
  const { jobTitle, yearsOfExperience, industry, skills, achievement, careerLevel } = data;
  const parsedSkills = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const lvl = levelVocab[careerLevel];
  const yrs = yearsLabel(yearsOfExperience);
  const achieveClause = buildAchievementClause(achievement);
  const domain = pickRandom(indCtx.domains);
  const impact = pickRandom(indCtx.impacts);

  const templates = [
    // Template 1
    () => {
      const skillIntro = parsedSkills.length > 0
        ? `${formatSkillsList(parsedSkills)} specialist`
        : `${domain} specialist`;
      const experiencePart = yrs
        ? `with ${yrs} of hands-on experience as a ${jobTitle} in ${industry}`
        : `with a strong foundation as a ${jobTitle} in ${industry}`;
      const ending = achieveClause
        ? `Combines technical depth with business acumen to deliver results, ${achieveClause}.`
        : `Combines technical depth with business acumen to deliver measurable improvements in ${impact}.`;
      return `${skillIntro.charAt(0).toUpperCase() + skillIntro.slice(1)} ${experiencePart}. ${ending}`;
    },
    // Template 2
    () => {
      const strength = pickRandom(lvl.strengthWords);
      const opener = parsedSkills.length > 0
        ? `${jobTitle} with ${strength} spanning ${formatSkillsList(parsedSkills)}`
        : `${jobTitle} with ${strength} in ${domain}`;
      const middle = yrs
        ? `Backed by ${yrs} of ${industry} experience, excels at translating complex challenges into streamlined solutions`
        : `Excels at translating complex ${industry} challenges into streamlined solutions`;
      const ending = achieveClause
        ? `Proven impact, ${achieveClause}.`
        : `Passionate about leveraging skills to improve ${impact}.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 3
    () => {
      const descriptor = pickRandom(lvl.descriptors);
      const skillPart = parsedSkills.length > 0
        ? `Brings specialized expertise in ${formatSkillsList(parsedSkills)}`
        : `Brings specialized expertise in ${domain}`;
      const opener = yrs
        ? `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} with ${yrs} of ${industry} experience`
        : `${descriptor.charAt(0).toUpperCase() + descriptor.slice(1)} ${jobTitle} in the ${industry} space`;
      const ending = achieveClause
        ? `${skillPart}, applying them to achieve tangible outcomes, ${achieveClause}.`
        : `${skillPart}, applying them to drive ${impact} and advance organizational goals.`;
      return `${opener}. ${ending}`;
    },
    // Template 4
    () => {
      const verb = pickRandom(indCtx.verbs);
      const opener = parsedSkills.length > 0
        ? `Proficient in ${formatSkillsList(parsedSkills)}, with a career built on delivering excellence as a ${jobTitle}`
        : `Career built on delivering excellence as a ${jobTitle} in ${domain}`;
      const middle = yrs
        ? `Over ${yrs} in ${industry}, has ${verb} solutions that directly improved ${impact}`
        : `Has ${verb} solutions in ${industry} that directly improved ${impact}`;
      const ending = achieveClause
        ? `Key contributions include ${achieveClause.replace(/^notably |^including |^highlighted by |^with a key accomplishment of |^such as /, '')}.`
        : `Known for a hands-on, results-oriented approach to every project.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 5
    () => {
      const opening = pickRandom(lvl.openings);
      const domain2 = pickRandom(indCtx.domains);
      const skillPart = parsedSkills.length > 0
        ? `Equipped with advanced skills in ${formatSkillsList(parsedSkills)}`
        : `Equipped with strong capabilities in ${domain} and ${domain2}`;
      const experiencePart = yrs
        ? `${opening} ${jobTitle} with ${yrs} honing expertise across ${domain} and ${domain2}`
        : `${opening} ${jobTitle} with expertise spanning ${domain} and ${domain2}`;
      const ending = achieveClause
        ? `${skillPart}, consistently delivering value, ${achieveClause}.`
        : `${skillPart}, consistently delivering value that advances ${impact}.`;
      return `${experiencePart}. ${ending}`;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Core summary generation — Story-Driven style                       */
/* ------------------------------------------------------------------ */

function generateStoryDriven(data: FormData): string {
  const { jobTitle, yearsOfExperience, industry, skills, achievement, careerLevel } = data;
  const parsedSkills = parseSkills(skills);
  const indCtx = industryContext[industry] || industryContext.Other;
  const lvl = levelVocab[careerLevel];
  const yrs = yearsLabel(yearsOfExperience);
  const achieveClause = buildAchievementClause(achievement);
  const domain = pickRandom(indCtx.domains);
  const impact = pickRandom(indCtx.impacts);

  const templates = [
    // Template 1
    () => {
      const opener = yrs
        ? `With ${yrs} of experience in ${industry}, I have built a career as a ${jobTitle} driven by a passion for ${domain}`
        : `A ${jobTitle} driven by a genuine passion for ${domain} in the ${industry} sector`;
      const middle = parsedSkills.length > 0
        ? `Along the way, I have developed deep proficiency in ${formatSkillsList(parsedSkills)}, using these skills to make a meaningful impact on ${impact}`
        : `This journey has shaped a deep understanding of what it takes to make a meaningful impact on ${impact}`;
      const ending = achieveClause
        ? `Most recently, I ${achieveClause.replace(/^notably |^including |^highlighted by |^with a key accomplishment of |^such as /, '')}, and I am eager to bring this momentum to a new challenge.`
        : `I am eager to bring this experience and momentum to a team where I can continue to grow and contribute.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 2
    () => {
      const descriptor = pickRandom(lvl.descriptors);
      const opener = yrs
        ? `What started as a career in ${industry} ${yrs} ago has evolved into a focused expertise in ${domain}`
        : `A career in ${industry} has evolved into a focused expertise in ${domain}`;
      const middle = parsedSkills.length > 0
        ? `As a ${descriptor} ${jobTitle}, I bring a unique blend of ${formatSkillsList(parsedSkills)} to every challenge`
        : `As a ${descriptor} ${jobTitle}, I bring a well-rounded approach to every challenge`;
      const ending = achieveClause
        ? `My proudest accomplishment: ${achieveClause.replace(/^notably |^including |^highlighted by |^with a key accomplishment of |^such as /, '')}.`
        : `I thrive at the intersection of strategy and execution, where I can drive real ${impact}.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 3
    () => {
      const verb = pickRandom(indCtx.verbs);
      const experiencePart = yrs
        ? `Over the course of ${yrs} in ${industry}, I have ${verb} solutions, built relationships, and grown into a ${jobTitle} who understands both the big picture and the details`
        : `Through my career in ${industry}, I have ${verb} solutions, built relationships, and grown into a ${jobTitle} who understands both the big picture and the details`;
      const skillPart = parsedSkills.length > 0
        ? `My core strengths -- ${formatSkillsList(parsedSkills)} -- allow me to bridge gaps between teams and deliver on ${impact}`
        : `My ability to bridge gaps between teams has consistently allowed me to deliver on ${impact}`;
      const ending = achieveClause
        ? `A career highlight includes ${achieveClause.replace(/^notably |^including |^highlighted by |^with a key accomplishment of |^such as /, '')}.`
        : `I am looking for a role where I can continue making a tangible difference.`;
      return `${experiencePart}. ${skillPart}. ${ending}`;
    },
    // Template 4
    () => {
      const strength = pickRandom(lvl.strengthWords);
      const opener = yrs
        ? `I am a ${jobTitle} with ${yrs} of experience shaped by a genuine curiosity about ${domain} and how it can transform ${industry}`
        : `I am a ${jobTitle} shaped by a genuine curiosity about ${domain} and how it can transform the ${industry} landscape`;
      const middle = parsedSkills.length > 0
        ? `My ${strength} in ${formatSkillsList(parsedSkills)} has been forged through real-world problem-solving and collaboration`
        : `My ${strength} has been forged through real-world problem-solving and cross-functional collaboration`;
      const ending = achieveClause
        ? `This approach has led to meaningful outcomes, ${achieveClause}.`
        : `This approach consistently produces improvements in ${impact} that matter to stakeholders.`;
      return `${opener}. ${middle}. ${ending}`;
    },
    // Template 5
    () => {
      const descriptor = pickRandom(lvl.descriptors);
      const impact2 = pickRandom(indCtx.impacts);
      const opener = yrs
        ? `${yrs.charAt(0).toUpperCase() + yrs.slice(1)} in ${industry} have taught me that the best ${jobTitle} professionals combine technical skill with empathy and clear communication`
        : `The most effective ${jobTitle} professionals combine technical skill with empathy and clear communication -- a philosophy that drives my work in ${industry}`;
      const middle = parsedSkills.length > 0
        ? `I have honed my abilities in ${formatSkillsList(parsedSkills)} while staying focused on what matters most: ${impact} and ${impact2}`
        : `I stay focused on what matters most: ${impact} and ${impact2}`;
      const ending = achieveClause
        ? `My ${descriptor} approach has delivered real-world results, ${achieveClause}.`
        : `I bring a ${descriptor} mindset to every project and am ready to contribute from day one.`;
      return `${opener}. ${middle}. ${ending}`;
    },
  ];

  return pickRandom(templates)();
}

/* ------------------------------------------------------------------ */
/*  Main generation dispatcher                                         */
/* ------------------------------------------------------------------ */

const styleGenerators: Record<SummaryStyle, (data: FormData) => string> = {
  'Results-Driven': generateResultsDriven,
  'Skills-Focused': generateSkillsFocused,
  'Story-Driven': generateStoryDriven,
};

const styleDescriptions: Record<SummaryStyle, string> = {
  'Results-Driven': 'Leads with experience and quantifiable impact. Best for roles where metrics and achievements matter most.',
  'Skills-Focused': 'Highlights your technical and functional expertise. Ideal when your skill set is your biggest differentiator.',
  'Story-Driven': 'Connects your career journey to your value proposition. Great for career changers or narrative-driven industries.',
};

function generateAllSummaries(data: FormData): GeneratedSummary[] {
  const styles: SummaryStyle[] = ['Results-Driven', 'Skills-Focused', 'Story-Driven'];
  return styles.map((style) => ({
    style,
    description: styleDescriptions[style],
    text: styleGenerators[style](data),
  }));
}

function regenerateSingle(data: FormData, style: SummaryStyle): string {
  return styleGenerators[style](data);
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Marketing',
  'Education',
  'Sales',
  'Engineering',
  'Design',
  'HR',
  'Other',
];

const CAREER_LEVELS: CareerLevel[] = ['Entry', 'Mid', 'Senior', 'Executive'];

const CAREER_LEVEL_LABELS: Record<CareerLevel, string> = {
  Entry: 'Entry Level',
  Mid: 'Mid Level',
  Senior: 'Senior Level',
  Executive: 'Executive',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function SummaryGeneratorContent() {
  const [jobTitle, setJobTitle] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [industry, setIndustry] = useState('Technology');
  const [skills, setSkills] = useState('');
  const [achievement, setAchievement] = useState('');
  const [careerLevel, setCareerLevel] = useState<CareerLevel>('Mid');
  const [summaries, setSummaries] = useState<GeneratedSummary[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const formData: FormData = {
    jobTitle: jobTitle.trim() || 'Professional',
    yearsOfExperience,
    industry,
    skills,
    achievement,
    careerLevel,
  };

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobTitle.trim()) return;
    const result = generateAllSummaries(formData);
    setSummaries(result);
    setHasGenerated(true);
  };

  const handleRegenerate = (index: number) => {
    const updated = [...summaries];
    updated[index] = {
      ...updated[index],
      text: regenerateSingle(formData, updated[index].style),
    };
    setSummaries(updated);
  };

  const styleBadgeColor: Record<SummaryStyle, string> = {
    'Results-Driven': 'bg-amber-100 text-amber-700',
    'Skills-Focused': 'bg-teal-100 text-teal-700',
    'Story-Driven': 'bg-purple-100 text-purple-700',
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Summary Generator"
        description="Generate 3 professional resume summary options tailored to your target role. Choose the style that best fits your career story."
        category="Resume"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ── Input Form ── */}
        <form
          onSubmit={handleGenerate}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Tell us about your career
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Job Title */}
            <div className="sm:col-span-2">
              <label htmlFor="jobTitle" className={labelClass}>
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                id="jobTitle"
                type="text"
                required
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g., Product Manager, Data Engineer, UX Designer"
                className={inputClass}
              />
            </div>

            {/* Years of Experience */}
            <div>
              <label htmlFor="yearsOfExperience" className={labelClass}>
                Years of Experience
              </label>
              <select
                id="yearsOfExperience"
                value={yearsOfExperience}
                onChange={(e) => setYearsOfExperience(e.target.value)}
                className={inputClass}
              >
                <option value="">Select...</option>
                <option value="1">1 year</option>
                <option value="2">2 years</option>
                <option value="3">3 years</option>
                <option value="5">5 years</option>
                <option value="7">7 years</option>
                <option value="10">10 years</option>
                <option value="15">15+ years</option>
                <option value="20">20+ years</option>
              </select>
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className={labelClass}>
                Industry
              </label>
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className={inputClass}
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind}
                  </option>
                ))}
              </select>
            </div>

            {/* Top Skills */}
            <div className="sm:col-span-2">
              <label htmlFor="skills" className={labelClass}>
                Top 3 Skills{' '}
                <span className="text-gray-400 font-normal">(comma-separated)</span>
              </label>
              <input
                id="skills"
                type="text"
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="e.g., Python, Data Analysis, Project Management"
                className={inputClass}
              />
            </div>

            {/* Key Achievement */}
            <div className="sm:col-span-2">
              <label htmlFor="achievement" className={labelClass}>
                Key Achievement{' '}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="achievement"
                type="text"
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                placeholder="e.g., Grew revenue by 40% in 12 months, Led a team of 15 engineers"
                className={inputClass}
              />
            </div>
          </div>

          {/* Career Level Radio Buttons */}
          <div className="mt-6">
            <span className={labelClass}>Career Level</span>
            <div className="flex flex-wrap gap-3 mt-2">
              {CAREER_LEVELS.map((level) => (
                <label
                  key={level}
                  className={`flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                    careerLevel === level
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="careerLevel"
                    value={level}
                    checked={careerLevel === level}
                    onChange={(e) => setCareerLevel(e.target.value as CareerLevel)}
                    className="accent-blue-600"
                  />
                  {CAREER_LEVEL_LABELS[level]}
                </label>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            disabled={!jobTitle.trim()}
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 px-10 rounded-xl shadow-lg transition-all text-lg"
          >
            Generate Summaries
          </button>
        </form>

        {/* ── Results ── */}
        {hasGenerated && summaries.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
              Your Resume Summaries
            </h2>
            <p className="text-gray-500 mb-8">
              Three styles, one role. Pick the summary that best represents you.
            </p>

            <div className="space-y-6">
              {summaries.map((summary, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:border-blue-200 hover:shadow-xl transition-all"
                >
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <span
                      className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${styleBadgeColor[summary.style]}`}
                    >
                      {summary.style}
                    </span>
                    <span className="text-sm text-gray-500">
                      {summary.description}
                    </span>
                  </div>

                  {/* Summary Text */}
                  <p className="text-gray-800 leading-relaxed text-base mb-5">
                    {summary.text}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <CopyButton text={summary.text} label="Copy" />
                    <button
                      onClick={() => handleRegenerate(idx)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Regenerate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Educational Section ── */}
        {hasGenerated && (
          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-6 sm:p-8">
            <h3 className="text-lg font-bold text-blue-900 mb-4">
              What Makes a Great Resume Summary
            </h3>
            <p className="text-blue-800 mb-5 leading-relaxed">
              Your resume summary is the first thing recruiters read. A strong summary can make the
              difference between landing an interview and getting skipped. Here is what separates a
              good summary from a great one:
            </p>
            <ul className="space-y-3 text-sm text-blue-800">
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">1.</span>
                <span>
                  <strong>Lead with your value, not your job title.</strong> Instead of &ldquo;Software
                  Engineer with 5 years of experience,&rdquo; try &ldquo;Software Engineer who has
                  reduced deployment times by 60% and shipped features used by 2M+ users.&rdquo;
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">2.</span>
                <span>
                  <strong>Include numbers whenever possible.</strong> Metrics make your claims
                  concrete. Revenue generated, team size, efficiency gains, customer impact --
                  anything you can quantify adds credibility.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">3.</span>
                <span>
                  <strong>Tailor it to the role.</strong> Your summary should mirror the language
                  and priorities in the job description. A generic summary is a missed opportunity
                  to show you are a perfect fit.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">4.</span>
                <span>
                  <strong>Keep it concise.</strong> Two to three sentences is the sweet spot.
                  Recruiters spend an average of 7 seconds on a first scan -- make every word earn
                  its place.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-500 font-bold mt-0.5 shrink-0">5.</span>
                <span>
                  <strong>Match the tone to your level.</strong> Early-career summaries should
                  emphasize eagerness, skills, and potential. Senior-level summaries should project
                  authority, strategy, and proven results.
                </span>
              </li>
            </ul>
            <p className="mt-6 pt-4 border-t border-blue-200 text-sm text-blue-700/70">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/professional-resume-summary-examples" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: Professional Resume Summary Examples</a>
              {' · '}
              <a href="https://hbr.org/2014/12/how-to-write-a-resume-that-stands-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">HBR: How to Write a Resume That Stands Out</a>
              {' · '}
              <a href="https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/TopResumeTips/tips-for-writing-your-resume.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Guide (U.S. DOL)</a>
            </p>
          </div>
        )}

        {/* ── CTA ── */}
        <ToolCTA
          headline="Ready to build a resume that matches your summary?"
          description="Superpower Resume creates a complete, ATS-optimized resume tailored to your target role in minutes -- not hours."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
